import _HubDetailQuery from "../__generated__/HubDetailQuery.graphql";
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Mic, X } from 'lucide-react';
import { En } from '../i18n/En';
import { formatDate } from '../i18n/format';
import { t } from '../i18n/t';
import { ActivityFeed } from './ActivityFeed';
import { HearIt } from './HearIt';
import { exemplarFor, kidCard, kindLabel, patternAbout, patternLabel, type KidCard } from './practice.ts';
import { SayItBack } from './SayItBack.tsx';
import { SayWithMe } from './SayWithMe.tsx';
import { Pronunciation } from './VocabularyDetail';
import { registerFor } from '../profile/register';
import type { HubDetailQuery } from '../__generated__/HubDetailQuery.graphql';
const HubDetailItem = (_HubDetailQuery.hash && _HubDetailQuery.hash !== "43371c6c22c347fc5b8864fcb68fcd11" && console.error("The definition of 'HubDetailQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _HubDetailQuery);

/**
 * One pattern in full: what the learner said, what it should have been, what
 * has happened to it since, and the way into practising it.
 *
 * The recorder here is "Say it back" (say-it-back spec), and it is not the
 * one that was deleted. That one was inert in four independent ways — a mic
 * button with no `onClick`, a POST to a `/speech` route that does not exist,
 * called with an empty frame list, falling back to asserting the learner had
 * said the target perfectly — and it fed an outcome, which cannot work in
 * principle: see `usePractise` for why an outcome belongs to a conversation.
 * Say it back sends real frames to a real mutation, shows an empty transcript
 * as empty, and writes nothing.
 *
 * `onDismiss` decides the shape. With one it is the hub's overlay; without one
 * it is the page at `/practice/:itemId`. Both mount the same query, so a card
 * the learner already opened resolves from the store.
 */
export function HubDetail({
  itemId,
  onDismiss,
  onPractise,
  busy,
  why = null
}: {
  itemId: string;
  onDismiss?: () => void;
  onPractise: (itemId: string) => void;
  busy: boolean;
  /** Why "Practise this" could not start, from `usePractise`. */
  why?: string | null;
}) {
  const data = useLazyLoadQuery<HubDetailQuery>(HubDetailItem, {
    itemId
  });
  const item = data.hubItem;
  const register = registerFor(data.me?.learner?.defaultCharacter?.audience);

  // `node(id:)` answers the `Node` interface, so the inline fragment leaves
  // every field optional: an id that is not a practice item resolves to an
  // object with nothing in it rather than to null.
  if (!item?.id || !item.pattern) {
    return <p className="text-base-content/50 py-12 text-center">{t('hub-gone')}</p>;
  }
  const {
    id,
    pattern,
    kind,
    occurrences,
    lastPractisedAt,
    practisable
  } = item;
  const exemplar = exemplarFor(item.exemplar, register);
  const about = patternAbout(pattern);
  const example = item.patternExample ?? null;
  const close = onDismiss && <button className="btn btn-ghost btn-sm btn-circle size-(--hit-min)" aria-label={t('hub-close')} onClick={onDismiss}>
      <X aria-hidden className="size-4" />
    </button>;
  const practise = <>
      {/* The hub card's reason: false at Pre-A1, where the start is refused. */}
      {practisable && <button className="btn btn-primary min-h-(--hit-min) gap-2" disabled={busy} onClick={() => onPractise(id)}>
          <Mic aria-hidden className="size-5" />
          {busy ? t('common-starting') : t('practice-start-conversation')}
        </button>}
      {why && <p role="alert" className="text-error text-sm">
          {why}
        </p>}
    </>;
  const body = register === 'kid' ? <KidDetail card={kidCard({
    ...item,
    id,
    pattern
  })} about={about} close={close} practise={practise} /> : <div className="flex flex-col gap-4">
        <header className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold">{patternLabel(pattern)}</h2>
            {kind && <span className="badge badge-sm badge-ghost self-start">{kindLabel(kind)}</span>}
          </div>
          {close}
        </header>

        {about && <p className="text-base-content/70">{about}</p>}

        {!exemplar && example && <div>
            <p className="text-base-content/50 text-xs">{t('hub-example')}</p>
            <p className="font-medium">
              <En>{example.text}</En>
            </p>
            <HearIt target={{
        examplePracticeItemId: id
      }} />
          </div>}

        {exemplar && <div className="flex flex-col gap-3">
            <div>
              <p className="text-base-content/50 text-xs">{t('hub-said')}</p>
              <p className="text-base-content/70 line-through">
                <En>{exemplar.learnerForm}</En>
              </p>
            </div>
            <div>
              <p className="text-base-content/50 text-xs">{t('hub-should-say')}</p>
              <p className="font-medium">
                <En>{exemplar.targetForm}</En>
              </p>
              <Pronunciation ipa={exemplar.targetIpa} register={register} />
              <HearIt target={{
          practiceItemId: id
        }} />
              <SayItBack key={id} practiceItemId={id} target={exemplar.targetForm} />
            </div>
          </div>}

        <p className="text-base-content/50 text-xs">
          {t('practice-seen', {
        count: occurrences ?? 0
      })}
          {lastPractisedAt && ' ' + t('practice-last-practised', {
        date: formatDate(new Date(lastPractisedAt))
      })}
        </p>

        {practise}

        {/* Its own boundary: the feed reads the learner's sessions, which is a
          heavier query than the card above it, and the pattern and the button
          should not wait on the history to render. */}
        <Suspense fallback={<p className="text-base-content/50 py-4 text-sm">{t('hub-loading-activity')}</p>}>
          <ActivityFeed itemId={itemId} />
        </Suspense>
      </div>;
  if (!onDismiss) return body;
  return (
    // Top-anchored, not daisyUI's centred default: a centred box shorter than
    // the screen re-centres whenever the card grows, and Say it back's answer
    // grows it by ~164 px, which moved the button 82 px up under the finger
    // (BUILD.md, "Say it back holds still"). Anchored, the card only extends
    // downward.
    <div className="modal modal-open items-start" role="dialog">
      {/* `modal-backdrop` as a sibling button is daisyUI's own click-outside:
          it fills the frame behind the box and closes on click, which a plain
          overlay div would need a handler and a keyboard escape hatch to do. */}
      <div className="modal-box mt-[5dvh] max-h-[90dvh] max-w-lg">{body}</div>
      <button type="button" className="modal-backdrop" aria-label={t('hub-close')} onClick={onDismiss} />
    </div>
  );
}

/**
 * A pattern in the kid register (kid-practice §7): a picture, a sentence to
 * hear, and the pattern's name for a parent. Rendered from `kidCard` alone,
 * so no count, kind, exemplar or activity can reach it (§6).
 */
function KidDetail({
  card,
  about,
  close,
  practise
}: {
  card: KidCard;
  about: string | null;
  close: React.ReactNode;
  practise: React.ReactNode;
}) {
  const example = card.patternExample;
  return <div className="flex flex-col gap-4">
      {close && <div className="flex justify-end">{close}</div>}
      {example && <div className="flex flex-col items-center gap-3 text-center">
          {/* The sentence carries the meaning; a screen reader would
              otherwise say "dog face, red apple" first (§3.3). */}
          {example.picture && <p aria-hidden className="text-6xl leading-none">
              {example.picture}
            </p>}
          <p className="text-xl font-semibold">
            <En>{example.text}</En>
          </p>
          <SayWithMe key={card.id} target={{
        examplePracticeItemId: card.id
      }} />
        </div>}
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">{patternLabel(card.pattern)}</h2>
        {about && <p className="text-base-content/70">{about}</p>}
      </div>
      {practise}
    </div>;
}